import React from 'react';
import { Warning, CheckCircle } from '@phosphor-icons/react';

/**
 * Environment badge for the super-admin console.
 *
 * Detection is purely hostname-based so it works in any deploy target without
 * needing a build-time env var:
 *   - vooxy.app / www.vooxy.app                → PRODUCTION (subtle emerald pill)
 *   - *.preview.emergentagent.com              → PREVIEW  (prominent amber banner)
 *   - anything else (localhost, custom hosts)  → LOCAL   (grey banner)
 *
 * The point of this component is to prevent the exact mistake that happened
 * once: super-admin ends up editing pricing on preview thinking it's production,
 * and the changes silently do nothing on the live site.
 */
export default function EnvironmentBadge({ variant = 'banner' }) {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProd = host === 'vooxy.app' || host === 'www.vooxy.app';
  const isPreview = host.endsWith('.preview.emergentagent.com');
  const isLocal = !isProd && !isPreview;

  if (variant === 'pill') {
    if (isProd) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
          data-testid="env-badge-pill"
          title="You are editing the live production environment"
        >
          <CheckCircle size={10} weight="fill" /> Production
        </span>
      );
    }
    if (isPreview) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-400/20 text-amber-300 border border-amber-400/40 animate-pulse"
          data-testid="env-badge-pill"
          title="Preview — edits here do not affect vooxy.app"
        >
          <Warning size={10} weight="fill" /> Preview
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-slate-500/20 text-slate-300 border border-slate-500/30"
        data-testid="env-badge-pill"
      >
        Local
      </span>
    );
  }

  // Banner variant — mounted at the top of the super-admin dashboard.
  // Production intentionally renders nothing to keep the console clean; a small
  // pill is shown in the header via <EnvironmentBadge variant="pill" />.
  if (isProd) return null;
  if (isPreview) {
    return (
      <div
        className="w-full bg-amber-500 text-[#3F1D00] px-6 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 shadow-sm"
        data-testid="env-banner-preview"
        role="status"
      >
        <Warning size={16} weight="fill" />
        You are editing the <b>PREVIEW</b> environment. Changes here do <u>not</u> affect vooxy.app —
        <a
          href={window.location.href.replace(host, 'vooxy.app')}
          className="ml-1 underline hover:no-underline decoration-2 underline-offset-2"
        >
          switch to production →
        </a>
      </div>
    );
  }
  return (
    <div
      className="w-full bg-slate-800 text-slate-100 px-6 py-2 text-xs font-medium flex items-center justify-center gap-2"
      data-testid="env-banner-local"
      role="status"
    >
      Local development environment — {host}
    </div>
  );
}
